import React, { useState, useRef, useEffect, useMemo, useCallback } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import { 
  User, 
  MapPin, 
  Briefcase, 
  Users, 
  Baby, 
  PenTool, 
  CheckCircle, 
  AlertCircle, 
  Trash2, 
  Plus, 
  ChevronRight, 
  ChevronLeft,
  FileText,
  ArrowLeftToLine
} from 'lucide-react';
import { Button, Card, Input, InputLabel, Select, Modal, StatusBadge } from './DesignSystem';
import { SupportErrorMessage } from '../../../components/overlays/EngineeringSupportBanner';
import { useShellProfile } from '../../../state/useShellProfile';
import {
    domainRouterApi,
    useAppendEnrollmentToApplicationMutation,
    useGetAdmissionsApplicationQuery,
} from '../../../state/domainRouterApi';
import { useAppDispatch } from '../../../state/hooks';
import { useEnrollmentProgress } from '@triedge/bridge/hooks';
import { openTextPdfInNewTab } from '../../../utils/pdf';
import { isEnrollmentRegistrationFormComplete } from '../../../parent-portal/enrollmentProgressSource';
import {
  hasCompleteEnrollmentParentName,
  hasEnrollmentParentProfileInformation,
  requiresEnrollmentParentName,
  resolveEnrollmentSignatureRequirement,
} from './enrollmentSignatureRequirement';
import {
  type EnrollmentIntakeValidationResult,
  validateEnrollmentIntakeStep,
} from './enrollmentIntakeValidation';
import {
  getValidationIndicatorClassName,
  getValidationIndicatorState,
  isValidEmailAddress,
  isValidPhoneNumber,
  normalizePhoneForSubmission,
  normalizePhoneInput,
  pickFirstValidEmail,
  pickFirstValidPhone,
} from '../../shared/contactValidation';
import {
  hasUnsavedFormData,
  useUnsavedBrowserNavigationGuard,
} from '../../../shared/forms/useUnsavedBrowserNavigationGuard';

// --- Constants ---
const US_STATES = [
  'Alabama',
  'Alaska',
  'Arizona',
  'Arkansas',
  'California',
  'Colorado',
  'Connecticut',
  'Delaware',
  'Florida',
  'Georgia',
  'Hawaii',
  'Idaho',
  'Illinois',
  'Indiana',
  'Iowa',
  'Kansas',
  'Kentucky',
  'Louisiana',
  'Maine',
  'Maryland',
  'Massachusetts',
  'Michigan',
  'Minnesota',
  'Mississippi',
  'Missouri',
  'Montana',
  'Nebraska',
  'Nevada',
  'New Hampshire',
  'New Jersey',
  'New Mexico',
  'New York',
  'North Carolina',
  'North Dakota',
  'Ohio',
  'Oklahoma',
  'Oregon',
  'Pennsylvania',
  'Rhode Island',
  'South Carolina',
  'South Dakota',
  'Tennessee',
  'Texas',
  'Utah',
  'Vermont',
  'Virginia',
  'Washington',
  'West Virginia',
  'Wisconsin',
  'Wyoming',
];

const US_STATE_ABBREVIATIONS: Record<string, string> = {
  AL: 'Alabama',
  AK: 'Alaska',
  AZ: 'Arizona',
  AR: 'Arkansas',
  CA: 'California',
  CO: 'Colorado',
  CT: 'Connecticut',
  DE: 'Delaware',
  FL: 'Florida',
  GA: 'Georgia',
  HI: 'Hawaii',
  ID: 'Idaho',
  IL: 'Illinois',
  IN: 'Indiana',
  IA: 'Iowa',
  KS: 'Kansas',
  KY: 'Kentucky',
  LA: 'Louisiana',
  ME: 'Maine',
  MD: 'Maryland',
  MA: 'Massachusetts',
  MI: 'Michigan',
  MN: 'Minnesota',
  MS: 'Mississippi',
  MO: 'Missouri',
  MT: 'Montana',
  NE: 'Nebraska',
  NV: 'Nevada',
  NH: 'New Hampshire',
  NJ: 'New Jersey',
  NM: 'New Mexico',
  NY: 'New York',
  NC: 'North Carolina',
  ND: 'North Dakota',
  OH: 'Ohio',
  OK: 'Oklahoma',
  OR: 'Oregon',
  PA: 'Pennsylvania',
  RI: 'Rhode Island',
  SC: 'South Carolina',
  SD: 'South Dakota',
  TN: 'Tennessee',
  TX: 'Texas',
  UT: 'Utah',
  VT: 'Vermont',
  VA: 'Virginia',
  WA: 'Washington',
  WV: 'West Virginia',
  WI: 'Wisconsin',
  WY: 'Wyoming',
};

// --- Types ---

interface FamilyInfo {
    familyNameEnglish: string;
    familyNameHebrew: string;
    address: string;
    city: string;
    state: string;
    zip: string;
    homePhone: string;
}

interface ParentInfo {
    title: string;
    firstName: string;
    lastName: string;
    occupation: string;
    workPhone: string;
    cellPhone: string;
    email: string;
}

interface Student {
    id: string;
    englishName: string;
    grade: string;
    dob: string;
    hebrewName: string;
    hebrewDob: string;
    isWithdrawn: boolean;
}

interface GrandparentInfo {
    title: string;
    name: string;
    address: string;
    city: string;
    state: string;
    zip: string;
    phone: string;
    email: string;
}

interface Sibling {
    id: string;
    name: string;
    age: string;
    school: string;
}

interface Signatures {
    fatherSignature: string | null; // dataURL
    motherSignature: string | null; // dataURL
    date: string;
}

interface RegistrationData {
    family: FamilyInfo;
    father: ParentInfo;
    mother: ParentInfo;
    students: Student[];
    paternalGrandparents: GrandparentInfo;
    maternalGrandparents: GrandparentInfo;
    siblings: Sibling[];
    signatures: Signatures;
}

const createEmptyRegistrationData = (): RegistrationData => ({
    family: {
        familyNameEnglish: "",
        familyNameHebrew: "",
        address: "",
        city: "",
        state: "",
        zip: "",
        homePhone: ""
    },
    father: {
        title: "",
        firstName: "",
        lastName: "",
        occupation: "",
        workPhone: "",
        cellPhone: "",
        email: ""
    },
    mother: {
        title: "",
        firstName: "",
        lastName: "",
        occupation: "",
        workPhone: "",
        cellPhone: "",
        email: ""
    },
    students: [],
    paternalGrandparents: { title: "", name: "", address: "", city: "", state: "", zip: "", phone: "", email: "" },
    maternalGrandparents: { title: "", name: "", address: "", city: "", state: "", zip: "", phone: "", email: "" },
    siblings: [],
    signatures: {
        fatherSignature: null,
        motherSignature: null,
        date: new Date().toLocaleDateString()
    }
});

const readText = (value: unknown): string => String(value ?? '').trim();

const joinClasses = (...classes: Array<string | false | null | undefined>): string =>
    classes.filter(Boolean).join(' ');

const normalizeZipForDomain = (value: unknown): string => {
    const digits = readText(value).replace(/[^\d]/g, '');
    if (digits.length >= 5) return digits.slice(0, 5);
    return '00000';
};

const normalizePhoneOptional = (value: unknown): string | undefined => {
    return normalizePhoneForSubmission(value);
};

const normalizePhoneRequired = (value: unknown, label: string): string => {
    const normalized = normalizePhoneOptional(value);
    if (!normalized) {
        throw new Error(`${label} must include a valid phone number.`);
    }
    return normalized;
};

const normalizeZipRequired = (value: unknown, label: string): string => {
    const digits = readText(value).replace(/[^\d]/g, '');
    if (digits.length < 5) {
        throw new Error(`${label} is required.`);
    }
    return digits.slice(0, 5);
};

const deriveFamilyNameFromProfile = (value: unknown): string => {
    const normalized = readText(value);
    if (!normalized) return '';
    const parts = normalized.split(/\s+/).filter(Boolean);
    return parts.length > 1 ? parts[parts.length - 1] : parts[0];
};

const asRecord = (value: unknown): Record<string, unknown> =>
    value && typeof value === 'object' && !Array.isArray(value) ? (value as Record<string, unknown>) : {};

const toDateInputValue = (unixSeconds: unknown): string => {
    const numeric = Number(unixSeconds);
    if (!Number.isFinite(numeric) || numeric <= 0) return '';
    const date = new Date(Math.floor(numeric) * 1000);
    if (Number.isNaN(date.getTime())) return '';
    const year = date.getUTCFullYear();
    const month = `${date.getUTCMonth() + 1}`.padStart(2, '0');
    const day = `${date.getUTCDate()}`.padStart(2, '0');
    return `${year}-${month}-${day}`;
};

const toGradeLabel = (value: unknown): string => {
    const token = readText(value).toLowerCase();
    if (!token) return 'Grade 1';
    if (token === 'nursery') return 'Nursery';
    if (token === 'pre_k') return 'Pre-K';
    if (token === 'kindergarten') return 'Kindergarten';
    const gradeMatch = token.match(/grade[_\s-]?([1-8])/);
    if (gradeMatch) return `Grade ${gradeMatch[1]}`;
    return readText(value);
};

const splitLocationParts = (value: unknown): { city: string; state: string; zip: string } => {
    const normalized = readText(value);
    if (!normalized) return { city: '', state: '', zip: '' };
    const [city = '', state = '', zip = ''] = normalized
        .split(',')
        .map((part) => part.trim())
        .filter(Boolean);
    return { city, state, zip };
};

const isEnrollmentEligibleStatus = (status: unknown): boolean => {
    const normalized = readText(status).toLowerCase();
    return normalized === 'accepted' || normalized === 'accepted_pending_enrollment';
};

const normalizeStateLabel = (value: unknown): string => {
    const normalized = readText(value);
    if (!normalized) return '';
    if (US_STATES.includes(normalized)) {
        return normalized;
    }
    const abbreviation = normalized.toUpperCase();
    if (US_STATE_ABBREVIATIONS[abbreviation]) {
        return US_STATE_ABBREVIATIONS[abbreviation];
    }
    return normalized;
};

const buildRegistrationPdfLines = (data: RegistrationData): string[] => {
    const lines: string[] = [
        'Family Information',
        `Family Name (English): ${readText(data.family.familyNameEnglish) || 'Not provided'}`,
        `Family Name (Hebrew): ${readText(data.family.familyNameHebrew) || 'Not provided'}`,
        `Address: ${readText(data.family.address) || 'Not provided'}`,
        `City: ${readText(data.family.city) || 'Not provided'}`,
        `State: ${readText(data.family.state) || 'Not provided'}`,
        `Zip: ${readText(data.family.zip) || 'Not provided'}`,
        `Primary Home Phone: ${readText(data.family.homePhone) || 'Not provided'}`,
        '',
        'Parents',
        `Father: ${readText(data.father.firstName)} ${readText(data.father.lastName)}`.trim() || 'Not provided',
        `Father Email: ${readText(data.father.email) || 'Not provided'}`,
        `Father Phone: ${readText(data.father.cellPhone) || readText(data.father.workPhone) || 'Not provided'}`,
        `Mother: ${readText(data.mother.firstName)} ${readText(data.mother.lastName)}`.trim() || 'Not provided',
        `Mother Email: ${readText(data.mother.email) || 'Not provided'}`,
        `Mother Phone: ${readText(data.mother.cellPhone) || readText(data.mother.workPhone) || 'Not provided'}`,
        '',
        'Grandparents',
        'Paternal Grandparents',
        `Name: ${readText(data.paternalGrandparents.name) || 'Not provided'}`,
        `City: ${readText(data.paternalGrandparents.city) || 'Not provided'}`,
        `State: ${readText(data.paternalGrandparents.state) || 'Not provided'}`,
        `Zip: ${readText(data.paternalGrandparents.zip) || 'Not provided'}`,
        `Phone: ${readText(data.paternalGrandparents.phone) || 'Not provided'}`,
        `Email: ${readText(data.paternalGrandparents.email) || 'Not provided'}`,
        '',
        'Maternal Grandparents',
        `Name: ${readText(data.maternalGrandparents.name) || 'Not provided'}`,
        `City: ${readText(data.maternalGrandparents.city) || 'Not provided'}`,
        `State: ${readText(data.maternalGrandparents.state) || 'Not provided'}`,
        `Zip: ${readText(data.maternalGrandparents.zip) || 'Not provided'}`,
        `Phone: ${readText(data.maternalGrandparents.phone) || 'Not provided'}`,
        `Email: ${readText(data.maternalGrandparents.email) || 'Not provided'}`,
        '',
        'Students',
    ];

    if (data.students.length === 0) {
        lines.push('No students were captured at this time.');
    } else {
        data.students.forEach((student, index) => {
            const status = student.isWithdrawn ? 'Withdrawn' : 'Active';
            lines.push(`#${index + 1}`);
            lines.push(`Name: ${readText(student.englishName) || 'Not provided'}`);
            lines.push(`Hebrew Name: ${readText(student.hebrewName) || 'Not provided'}`);
            lines.push(`Grade: ${readText(student.grade) || 'Not provided'}`);
            lines.push(`Date of Birth: ${readText(student.dob) || 'Not provided'}`);
            lines.push(`Hebrew DOB: ${readText(student.hebrewDob) || 'Not provided'}`);
            lines.push(`Enrollment Status: ${status}`);
        });
    }

    lines.push('', 'Siblings');
    if (data.siblings.length === 0) {
        lines.push('No sibling entries captured.');
    } else {
        data.siblings.forEach((sibling, index) => {
            lines.push(`#${index + 1}`);
            lines.push(`Name: ${readText(sibling.name) || 'Not provided'}`);
            lines.push(`Age: ${readText(sibling.age) || 'Not provided'}`);
            lines.push(`School: ${readText(sibling.school) || 'Not provided'}`);
        });
    }

    lines.push('', 'Signatures');
    lines.push(`Father Signature: ${data.signatures.fatherSignature ? 'Captured' : 'Not captured'}`);
    lines.push(`Mother Signature: ${data.signatures.motherSignature ? 'Captured' : 'Not captured'}`);
    lines.push(`Signature Date: ${readText(data.signatures.date) || 'Not provided'}`);

    return lines;
};

// --- Components ---

const ProgressBar = ({ currentStep, onStepClick }: { currentStep: number, onStepClick: (step: number) => void }) => {
    // UPDATED STEPS: Combined Parents, Moved Students after Siblings
    const steps = ["Family", "Parents", "Grandparents", "Siblings", "Students", "Signatures"];
    const progress = Math.round(((currentStep + 1) / steps.length) * 100);

    return (
        <div className="mb-8">
            <div className="flex justify-between items-end mb-2">
                <span className="text-[10px] font-bold text-irongrey/50 tracking-widest">REGISTRATION PROGRESS</span>
                <span className="text-[10px] font-bold text-irongrey/50 tracking-widest">{progress}%</span>
            </div>
            <div className="h-1.5 w-full bg-gray-200 rounded-full mb-3 overflow-hidden">
                <div 
                    className="h-full bg-primary rounded-full transition-all duration-500 ease-out" 
                    style={{ width: `${progress}%` }}
                ></div>
            </div>
            <div className="hidden md:flex justify-between relative">
                {steps.map((step, idx) => (
                    <button 
                        key={step} 
                        onClick={() => idx <= currentStep ? onStepClick(idx) : null}
                        disabled={idx > currentStep}
                        className={`text-[10px] font-bold tracking-wide text-left transition-colors ${
                            idx === currentStep ? 'text-primary' : 
                            idx < currentStep ? 'text-primary/70 cursor-pointer hover:text-primary' : 
                            'text-irongrey/30 cursor-not-allowed'
                        }`}
                    >
                        {idx + 1}. {step}
                    </button>
                ))}
            </div>
             {/* Mobile Step Indicator */}
             <div className="md:hidden text-center text-xs font-bold text-primary">
                Step {currentStep + 1}: {steps[currentStep]}
            </div>
        </div>
    );
};

const SectionHeader: React.FC<{ icon?: React.ElementType, title: string, subtitle?: string }> = ({ icon: Icon, title, subtitle }) => (
    <div className="flex items-start gap-4 mb-6">
        {Icon && <div className="p-2 bg-primary/10 rounded-lg text-primary"><Icon size={20} /></div>}
        <div>
            <h3 className="text-lg font-bold text-irongrey font-heading">{title}</h3>
            {subtitle && <p className="text-xs text-irongrey/50 mt-1">{subtitle}</p>}
        </div>
    </div>
);

// --- Signature Pad Component ---
const SignaturePad: React.FC<{ 
    label: string, 
    value: string | null, 
    onChange: (val: string | null) => void,
    className?: string,
}> = ({ label, value, onChange, className = '' }) => {
    const canvasRef = useRef<HTMLCanvasElement>(null);
    const [isDrawing, setIsDrawing] = useState(false);

    // Initialize canvas context
    useEffect(() => {
        const canvas = canvasRef.current;
        if (!canvas) return;
        
        const ctx = canvas.getContext('2d');
        if (!ctx) return;

        ctx.strokeStyle = '#404f4d';
        ctx.lineWidth = 2;
        ctx.lineCap = 'round';

        // Load existing signature if present
        if (value) {
            const img = new Image();
            img.src = value;
            img.onload = () => ctx.drawImage(img, 0, 0);
        }
    }, []);

    const getPos = (e: React.MouseEvent | React.TouchEvent | MouseEvent | TouchEvent) => {
        const canvas = canvasRef.current;
        if (!canvas) return { x: 0, y: 0 };
        const rect = canvas.getBoundingClientRect();
        
        let clientX, clientY;
        if ('touches' in e) {
            clientX = e.touches[0].clientX;
            clientY = e.touches[0].clientY;
        } else {
            clientX = (e as React.MouseEvent).clientX;
            clientY = (e as React.MouseEvent).clientY;
        }
        
        return {
            x: clientX - rect.left,
            y: clientY - rect.top
        };
    };

    const startDrawing = (e: React.MouseEvent | React.TouchEvent) => {
        setIsDrawing(true);
        const ctx = canvasRef.current?.getContext('2d');
        if (!ctx) return;
        const { x, y } = getPos(e);
        ctx.beginPath();
        ctx.moveTo(x, y);
    };

    const draw = (e: React.MouseEvent | React.TouchEvent) => {
        if (!isDrawing) return;
        const ctx = canvasRef.current?.getContext('2d');
        if (!ctx) return;
        const { x, y } = getPos(e);
        ctx.lineTo(x, y);
        ctx.stroke();
    };

    const stopDrawing = () => {
        if (!isDrawing) return;
        setIsDrawing(false);
        if (canvasRef.current) {
            onChange(canvasRef.current.toDataURL());
        }
    };

    const clear = () => {
        const canvas = canvasRef.current;
        if (!canvas) return;
        const ctx = canvas.getContext('2d');
        if (ctx) {
            ctx.clearRect(0, 0, canvas.width, canvas.height);
            onChange(null);
        }
    };

    return (
        <div className="w-full">
            <InputLabel>{label}</InputLabel>
            <div className={`border-2 border-dashed border-gray-200 rounded-xl bg-gray-50 touch-none overflow-hidden relative group hover:border-primary/30 transition-colors ${className}`}>
                <canvas
                    ref={canvasRef}
                    width={400}
                    height={160}
                    className="w-full h-40 cursor-crosshair"
                    onMouseDown={startDrawing}
                    onMouseMove={draw}
                    onMouseUp={stopDrawing}
                    onMouseLeave={stopDrawing}
                    onTouchStart={startDrawing}
                    onTouchMove={draw}
                    onTouchEnd={stopDrawing}
                />
                {!value && !isDrawing && (
                    <div className="absolute inset-0 flex items-center justify-center pointer-events-none text-irongrey/20 font-bold text-sm">
                        Sign here
                    </div>
                )}
                <button 
                    onClick={clear}
                    className="absolute top-2 right-2 p-1.5 rounded-lg bg-white shadow-sm border border-gray-100 text-irongrey/40 hover:text-strawberry hover:bg-strawberry/5 transition-colors"
                    title="Clear Signature"
                >
                    <Trash2 size={16} />
                </button>
            </div>
            <p className="text-[10px] text-irongrey/40 mt-1.5 text-right">Use mouse or finger to sign</p>
        </div>
    );
};

// --- Main Form Component ---

const StudentForm = () => {
    const [currentStep, setCurrentStep] = useState(0);
    const [formData, setFormData] = useState<RegistrationData>(() => createEmptyRegistrationData());
    const { profile } = useShellProfile();
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const applicationId = readText(searchParams.get('applicationId'));
    const childApplicationId = readText(searchParams.get('childApplicationId'));
    const editSubmittedRegistration = readText(searchParams.get('editRegistration')) === '1';
    const applicationQuery = useGetAdmissionsApplicationQuery(applicationId, { skip: !applicationId });
    const [appendEnrollmentToApplication, appendEnrollmentState] = useAppendEnrollmentToApplicationMutation();
    const enrollmentProgress = useEnrollmentProgress();
    const dispatch = useAppDispatch();
    // Keep a copy of initial data to detect edits
    const initialDataRef = useRef<RegistrationData>(createEmptyRegistrationData());
    const hydratedApplicationIdRef = useRef<string | null>(null);
    const [submissionError, setSubmissionError] = useState<string | null>(null);
    const [submittedCode, setSubmittedCode] = useState<string | null>(null);
    const [touchedFields, setTouchedFields] = useState<Record<string, boolean>>({});
    const [submitAttempted, setSubmitAttempted] = useState(false);

    // Modal state for student withdrawal
    const [withdrawModalOpen, setWithdrawModalOpen] = useState(false);
    const [studentToWithdraw, setStudentToWithdraw] = useState<string | null>(null);
    const fieldKey = useCallback((...parts: Array<string | number>) => parts.join('.'), []);
    const markFieldTouched = useCallback((fieldName: string) => {
        setTouchedFields((previous) => (
            previous[fieldName]
                ? previous
                : { ...previous, [fieldName]: true }
        ));
    }, []);
    const shouldShowFieldValidation = useCallback(
        (fieldName: string) => Boolean(touchedFields[fieldName] || submitAttempted),
        [submitAttempted, touchedFields],
    );
    const getFieldIndicatorClassName = useCallback(
        (
            fieldName: string,
            value: unknown,
            options?: {
                required?: boolean;
                validator?: (candidate: unknown) => boolean;
            },
        ) => getValidationIndicatorClassName(getValidationIndicatorState({
            show: shouldShowFieldValidation(fieldName),
            value,
            required: options?.required ?? true,
            validator: options?.validator,
        })),
        [shouldShowFieldValidation],
    );
    const getGroupedFieldIndicatorClassName = useCallback(
        (
            fieldName: string,
            value: unknown,
            groupRequiredMissing: boolean,
            validator?: (candidate: unknown) => boolean,
        ) => {
            if (!shouldShowFieldValidation(fieldName)) {
                return '';
            }
            const normalized = readText(value);
            if (!normalized) {
                return groupRequiredMissing
                    ? getValidationIndicatorClassName('missing')
                    : '';
            }
            if (validator && !validator(normalized)) {
                return getValidationIndicatorClassName('invalid');
            }
            return '';
        },
        [shouldShowFieldValidation],
    );

    const scrollToTop = () => {
        const root = document.getElementById('root')?.parentElement;
        if(root) root.scrollTo({ top: 0, behavior: 'smooth' });
    };

    const markValidationFieldsTouched = (fields: string[]) => {
        if (!fields.length) return;

        setTouchedFields((previous) => {
            let changed = false;
            const next = { ...previous };
            fields.forEach((fieldName) => {
                if (!next[fieldName]) {
                    next[fieldName] = true;
                    changed = true;
                }
            });
            return changed ? next : previous;
        });
    };

    const validateStep = (step: number): EnrollmentIntakeValidationResult => validateEnrollmentIntakeStep(
        formData,
        step,
        readText(profile?.email),
    );

    const showValidationFailure = (result: EnrollmentIntakeValidationResult) => {
        markValidationFieldsTouched(result.fields);
        setSubmitAttempted(true);
        setSubmissionError(result.message);
        setCurrentStep(result.step);
        scrollToTop();
    };

    const goToChecklist = () => {
        if (applicationId && childApplicationId) {
            const params = new URLSearchParams();
            params.set('applicationId', applicationId);
            params.set('childApplicationId', childApplicationId);
            navigate(`/parent/portal/enrollment?${params.toString()}`);
            return;
        }

        navigate('/parent/portal/enrollment');
    };

    const openSubmittedRegistrationEditor = () => {
        const params = new URLSearchParams(searchParams);
        params.set('editRegistration', '1');
        setSubmissionError(null);
        setSubmitAttempted(false);
        setCurrentStep(0);
        navigate(`/parent/portal--new/enrollment?${params.toString()}`);
        scrollToTop();
    };

    const invalidateEnrollmentApplicationCaches = useCallback(() => {
        dispatch(domainRouterApi.util.invalidateTags([
            { type: 'AdmissionsApplications' },
            ...(applicationId ? [{ type: 'AdmissionsApplication' as const, id: applicationId }] : []),
        ]));
    }, [applicationId, dispatch]);

    const handleDownloadPdf = async () => {
        await openTextPdfInNewTab({
            title: 'Registration Complete',
            lines: buildRegistrationPdfLines(formData),
            fileName: 'parent-portal-enrollment-complete.pdf',
        });
    };

    const selectedChild = useMemo(() => {
        const applicationRecord = asRecord(applicationQuery.data);
        const children = Array.isArray(applicationRecord.children) ? applicationRecord.children : [];
        const matched = children.find((entry) => readText(asRecord(entry).id) === childApplicationId);
        return asRecord(matched);
    }, [applicationQuery.data, childApplicationId]);

    const selectedChildStatus = readText(selectedChild.status);
    const applicationStatus = readText(asRecord(applicationQuery.data).status);
    const selectedChildEligible = isEnrollmentEligibleStatus(selectedChildStatus || applicationStatus);
    const registrationFormComplete = isEnrollmentRegistrationFormComplete(asRecord(selectedChild.enrollmentProgress));
    const isSubmitting = appendEnrollmentState.isLoading || enrollmentProgress.updating;
    const signatureRequirement = resolveEnrollmentSignatureRequirement({
        father: formData.father,
        mother: formData.mother,
        signatures: formData.signatures,
    });
    const shouldWarnBrowserBack = currentStep < 6
        && !submittedCode
        && !isSubmitting
        && hasUnsavedFormData(formData, initialDataRef.current);
    const browserBackGuard = useUnsavedBrowserNavigationGuard({ enabled: shouldWarnBrowserBack });

    useEffect(() => {
        if (!selectedChild.id || !registrationFormComplete || editSubmittedRegistration) return;
        const existingCode = readText(asRecord(applicationQuery.data).code);
        if (existingCode) setSubmittedCode(existingCode);
        setCurrentStep(6);
    }, [applicationQuery.data, editSubmittedRegistration, registrationFormComplete, selectedChild.id]);

    useEffect(() => {
        if (!applicationId) return;
        if (!applicationQuery.data) return;
        if (hydratedApplicationIdRef.current === applicationId) return;

        const applicationRecord = asRecord(applicationQuery.data);
        const applicant = asRecord(applicationRecord.applicant);
        const parents = Array.isArray(applicant.parents) ? applicant.parents.map((entry) => asRecord(entry)) : [];
        const father = parents.find((entry) => readText(entry.relation).toLowerCase() === 'father');
        const mother = parents.find((entry) => readText(entry.relation).toLowerCase() === 'mother');
        const primaryContact = asRecord(applicant.primaryContact);
        const primaryAddress = asRecord(primaryContact.address);
        const paternalGrandparents = asRecord(applicant.paternalGrandparents);
        const maternalGrandparents = asRecord(applicant.maternalGrandparents);
        const paternalLocation = splitLocationParts(paternalGrandparents.location);
        const maternalLocation = splitLocationParts(maternalGrandparents.location);
        const siblings = Array.isArray(applicant.otherSiblings)
            ? applicant.otherSiblings.map((entry, index) => {
                const sibling = asRecord(entry);
                return {
                    id: readText(sibling.id) || `existing-sibling-${index + 1}`,
                    name: readText(sibling.name),
                    age: readText(sibling.age),
                    school: readText(sibling.school),
                };
            })
            : [];
        const children = Array.isArray(applicationRecord.children)
            ? applicationRecord.children.map((entry, index) => {
                const child = asRecord(entry);
                const childInfo = asRecord(child.childInfo);
                const englishName = `${readText(childInfo.firstName)} ${readText(childInfo.lastName)}`.trim();
                const hebrewName = `${readText(childInfo.hebrewFirstName)} ${readText(childInfo.hebrewLastName)}`.trim();
                return {
                    id: readText(child.id) || `existing-student-${index + 1}`,
                    englishName: englishName || `Student ${index + 1}`,
                    grade: toGradeLabel(child.gradeApplyingFor),
                    dob: toDateInputValue(childInfo.dateOfBirth),
                    hebrewName,
                    hebrewDob: readText(childInfo.hebrewDateOfBirth),
                    isWithdrawn: false,
                };
            })
            : [];

        const nextData: RegistrationData = {
            family: {
                familyNameEnglish: readText(applicant.familyName),
                familyNameHebrew: readText(applicant.hebrewFamilyName),
                address: readText(primaryAddress.street),
                city: readText(primaryAddress.city),
                state: normalizeStateLabel(readText(primaryAddress.state) || 'New Jersey'),
                zip: readText(primaryAddress.zipCode),
                homePhone: readText(applicant.homePhone),
            },
            father: {
                title: readText(father?.title),
                firstName: readText(father?.firstName),
                lastName: readText(father?.lastName),
                occupation: readText(father?.occupation),
                workPhone: '',
                cellPhone: readText(father?.phone),
                email: readText(father?.email),
            },
            mother: {
                title: readText(mother?.title),
                firstName: readText(mother?.firstName),
                lastName: readText(mother?.lastName),
                occupation: readText(mother?.occupation),
                workPhone: '',
                cellPhone: readText(mother?.phone),
                email: readText(mother?.email),
            },
            students: children,
            paternalGrandparents: {
                title: '',
                name: readText(paternalGrandparents.names),
                address: '',
                city: paternalLocation.city,
                state: normalizeStateLabel(paternalLocation.state),
                zip: paternalLocation.zip,
                phone: readText(paternalGrandparents.phone),
                email: '',
            },
            maternalGrandparents: {
                title: '',
                name: readText(maternalGrandparents.names),
                address: '',
                city: maternalLocation.city,
                state: normalizeStateLabel(maternalLocation.state),
                zip: maternalLocation.zip,
                phone: readText(maternalGrandparents.phone),
                email: '',
            },
            siblings,
            signatures: {
                fatherSignature: null,
                motherSignature: null,
                date: new Date().toLocaleDateString(),
            },
        };

        setFormData(nextData);
        initialDataRef.current = nextData;
        hydratedApplicationIdRef.current = applicationId;
    }, [applicationId, applicationQuery.data]);

    useEffect(() => {
        if (!profile) return;
        const profileEmail = readText(profile.email);
        const profileFamilyName = deriveFamilyNameFromProfile(profile.name);
        if (!profileEmail && !profileFamilyName) return;

        setFormData((previous) => {
            const fatherEmail = readText(previous.father.email);
            const motherEmail = readText(previous.mother.email);
            const familyName = readText(previous.family.familyNameEnglish);
            const next: RegistrationData = {
                ...previous,
                father: { ...previous.father },
                mother: { ...previous.mother },
                family: { ...previous.family },
            };
            let changed = false;

            if (profileEmail) {
                const fatherListed = hasCompleteEnrollmentParentName(previous.father);
                const motherListed = hasCompleteEnrollmentParentName(previous.mother);
                const shouldFillFatherEmail = !fatherEmail && (fatherListed || !motherListed);
                const shouldFillMotherEmail = !motherEmail && motherListed;
                if (shouldFillFatherEmail) {
                    next.father.email = profileEmail;
                    changed = true;
                }
                if (shouldFillMotherEmail) {
                    next.mother.email = profileEmail;
                    changed = true;
                }
            }

            if (profileFamilyName && !familyName) {
                next.family.familyNameEnglish = profileFamilyName;
                changed = true;
            }

            return changed ? next : previous;
        });
    }, [profile]);

    const buildApplicantPatch = () => {
        const familyNameEnglish = readText(formData.family.familyNameEnglish);
        if (!familyNameEnglish) {
            throw new Error('Family name is required before submitting enrollment.');
        }

        const fatherFirst = readText(formData.father.firstName);
        const fatherLast = readText(formData.father.lastName);
        const motherFirst = readText(formData.mother.firstName);
        const motherLast = readText(formData.mother.lastName);
        const contactFirstName = fatherFirst || motherFirst;
        const contactLastName = fatherLast || motherLast || familyNameEnglish;
        if (!contactFirstName || !contactLastName) {
            throw new Error('At least one parent first and last name is required.');
        }

        const contactEmail = pickFirstValidEmail([
            formData.father.email,
            formData.mother.email,
            profile?.email,
        ]);
        if (!contactEmail) {
            throw new Error('A valid parent email is required before submitting enrollment.');
        }

        const homePhoneCandidate = pickFirstValidPhone([
            formData.family.homePhone,
            formData.father.cellPhone,
            formData.mother.cellPhone,
        ]);
        if (!homePhoneCandidate) {
            throw new Error('Home or parent phone must include a valid phone number.');
        }
        const homePhone = homePhoneCandidate;
        const street = readText(formData.family.address);
        const city = readText(formData.family.city);
        const state = readText(formData.family.state);
        if (!street || !city || !state) {
            throw new Error('Family address, city, and state are required before submitting enrollment.');
        }

        const profileEmail = readText(profile?.email);
        const parentRecords = [
            { relation: 'father' as const, source: formData.father },
            { relation: 'mother' as const, source: formData.mother },
        ].map((entry) => {
            const firstName = readText(entry.source.firstName);
            const lastName = readText(entry.source.lastName);
            const email = readText(entry.source.email);
            const phone = readText(entry.source.cellPhone) || readText(entry.source.workPhone);
            const occupation = readText(entry.source.occupation);
            const title = readText(entry.source.title);
            const parentOptions = { ignoredEmail: profileEmail };

            if (!hasEnrollmentParentProfileInformation(entry.source, parentOptions)) return null;
            if (requiresEnrollmentParentName(entry.source, parentOptions)) {
                throw new Error(`${entry.relation === 'father' ? "Father's" : "Mother's"} first and last name are required.`);
            }

            const normalizedPhone = normalizePhoneOptional(phone);
            return {
                title: title || undefined,
                firstName,
                lastName,
                relation: entry.relation,
                email: isValidEmailAddress(email) ? email : undefined,
                phone: normalizedPhone,
                occupation: occupation || undefined,
            };
        }).filter((entry): entry is NonNullable<typeof entry> => Boolean(entry));

        const siblings = formData.siblings
            .map((entry) => ({
                id: readText(entry.id) || undefined,
                name: readText(entry.name),
                age: readText(entry.age),
                school: readText(entry.school),
            }))
            .filter((entry) => entry.name || entry.school);

        const applicantPatch: Record<string, unknown> = {
            familyName: familyNameEnglish,
            hebrewFamilyName: readText(formData.family.familyNameHebrew) || undefined,
            primaryContact: {
                firstName: contactFirstName,
                lastName: contactLastName,
                email: contactEmail,
                phone: homePhone,
                address: {
                    street,
                    city,
                    state,
                    zipCode: normalizeZipRequired(formData.family.zip, 'Family ZIP code'),
                },
            },
            homePhone,
            parents: parentRecords,
            paternalGrandparents: {
                names: readText(formData.paternalGrandparents.name),
                location: `${readText(formData.paternalGrandparents.city)}, ${readText(formData.paternalGrandparents.state)}, ${readText(formData.paternalGrandparents.zip)}`.replace(/(,\s*){2,}/g, ', ').replace(/^,\s*|\s*,$/g, ''),
                phone: normalizePhoneOptional(formData.paternalGrandparents.phone),
                family: familyNameEnglish,
            },
            maternalGrandparents: {
                names: readText(formData.maternalGrandparents.name),
                location: `${readText(formData.maternalGrandparents.city)}, ${readText(formData.maternalGrandparents.state)}, ${readText(formData.maternalGrandparents.zip)}`.replace(/(,\s*){2,}/g, ', ').replace(/^,\s*|\s*,$/g, ''),
                phone: normalizePhoneOptional(formData.maternalGrandparents.phone),
                family: familyNameEnglish,
            },
        };
        if (siblings.length > 0) {
            applicantPatch.otherSiblings = siblings;
        }
        return applicantPatch;
    };

    const handleSubmitRegistration = async () => {
        setSubmitAttempted(true);
        setSubmissionError(null);
        if (!applicationId || !childApplicationId) {
            setSubmissionError('Select a student from Enrollment before submitting forms.');
            return;
        }
        if (!selectedChild.id) {
            setSubmissionError('Unable to resolve the selected student enrollment record.');
            return;
        }
        if (!selectedChildEligible) {
            setSubmissionError('This student is not eligible for enrollment yet. Please wait for school acceptance.');
            return;
        }

        for (const step of [0, 1]) {
            const validation = validateStep(step);
            if (!validation.valid) {
                showValidationFailure(validation);
                return;
            }
        }

        if (!signatureRequirement.valid) {
            setCurrentStep(5);
            setSubmissionError(signatureRequirement.message || 'A parent signature is required before submitting registration.');
            return;
        }
        try {
            await appendEnrollmentToApplication({
                applicationId,
                updates: {
                    applicant: buildApplicantPatch(),
                },
            }).unwrap();
            invalidateEnrollmentApplicationCaches();

            try {
                await enrollmentProgress.updateProgress({
                    childApplicationId,
                    step: 'step2RegistrationForm',
                    completed: true,
                });
            } catch {
                // Applicant append persisted; progress sync can be retried by staff/parent later.
            } finally {
                invalidateEnrollmentApplicationCaches();
            }

            const existingCode = readText(asRecord(applicationQuery.data).code);
            if (existingCode) setSubmittedCode(existingCode);
            setCurrentStep(6);
            scrollToTop();
        } catch (error) {
            const message = error instanceof Error ? error.message : 'Unable to submit registration right now.';
            setSubmissionError(message);
        }
    };

    const nextStep = () => {
        const validation = validateStep(currentStep);
        if (!validation.valid) {
            showValidationFailure(validation);
            return;
        }

        setSubmissionError(null);
        setSubmitAttempted(false);
        if (currentStep < 5) {
            setCurrentStep(prev => prev + 1);
            scrollToTop();
        }
    };

    const prevStep = () => {
        if (currentStep > 0) {
            setCurrentStep(prev => prev - 1);
            scrollToTop();
        }
    };

    // Helper to get input style based on state (Pre-filled vs Edited)
    const getInputClass = (currentValue: any, initialValue: any) => {
        if (currentValue !== initialValue) {
            return "bg-amber-50 border-amber-200 focus:border-amber-400 focus:ring-amber-400"; // Edited
        }
        if (initialValue && initialValue !== "") {
            return "bg-blue-50/50 border-blue-100 focus:border-primary focus:ring-primary"; // Pre-filled
        }
        return ""; // Standard
    };

    // --- Updaters ---

    const updateFamily = (field: keyof FamilyInfo, value: string) => {
        const nextValue = field === 'homePhone' ? normalizePhoneInput(value) : value;
        setFormData(prev => ({ ...prev, family: { ...prev.family, [field]: nextValue } }));
    };

    const updateParent = (parent: 'father' | 'mother', field: keyof ParentInfo, value: string) => {
        const nextValue =
            field === 'workPhone' || field === 'cellPhone'
                ? normalizePhoneInput(value)
                : value;
        setFormData(prev => ({ ...prev, [parent]: { ...prev[parent], [field]: nextValue } }));
    };

    const updateGrandparent = (side: 'paternalGrandparents' | 'maternalGrandparents', field: keyof GrandparentInfo, value: string) => {
        const nextValue = field === 'phone' ? normalizePhoneInput(value) : value;
        setFormData(prev => ({ ...prev, [side]: { ...prev[side], [field]: nextValue } }));
    };

    const handleWithdrawClick = (studentId: string, currentStatus: boolean) => {
        if (currentStatus) {
            // Currently withdrawn, re-enrolling is simple
            setFormData(prev => ({
                ...prev,
                students: prev.students.map(s => s.id === studentId ? { ...s, isWithdrawn: false } : s)
            }));
        } else {
            // Currently enrolled, attempting to withdraw -> Confirm
            setStudentToWithdraw(studentId);
            setWithdrawModalOpen(true);
        }
    };

    const confirmWithdraw = () => {
        if (studentToWithdraw) {
            setFormData(prev => ({
                ...prev,
                students: prev.students.map(s => s.id === studentToWithdraw ? { ...s, isWithdrawn: true } : s)
            }));
            setWithdrawModalOpen(false);
            setStudentToWithdraw(null);
        }
    };

    const addSibling = () => {
        const newSibling: Sibling = { id: crypto.randomUUID(), name: '', age: '', school: '' };
        setFormData(prev => ({ ...prev, siblings: [...prev.siblings, newSibling] }));
    };

    const removeSibling = (id: string) => {
        setFormData(prev => ({ ...prev, siblings: prev.siblings.filter(s => s.id !== id) }));
    };

    const updateSibling = (id: string, field: keyof Sibling, value: string) => {
        setFormData(prev => ({
            ...prev,
            siblings: prev.siblings.map(s => s.id === id ? { ...s, [field]: value } : s)
        }));
    };

    // --- Render Sections ---

    const renderFamilyStep = () => {
        const familyPhoneMissing = !pickFirstValidPhone([
            formData.family.homePhone,
            formData.father.cellPhone,
            formData.mother.cellPhone,
        ]);

        return (
            <div className="animate-in slide-in-from-bottom-4 fade-in duration-500">
                <SectionHeader
                    icon={MapPin}
                    title="Family Information"
                    subtitle="Please review and update your family's contact details."
                />
                <Card>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                        <div>
                            <InputLabel>Family Name (English)</InputLabel>
                            <Input
                                value={formData.family.familyNameEnglish}
                                onChange={e => updateFamily('familyNameEnglish', e.target.value)}
                                onBlur={() => markFieldTouched(fieldKey('family', 'familyNameEnglish'))}
                                className={joinClasses(
                                    getInputClass(formData.family.familyNameEnglish, initialDataRef.current.family.familyNameEnglish),
                                    getFieldIndicatorClassName(
                                        fieldKey('family', 'familyNameEnglish'),
                                        formData.family.familyNameEnglish,
                                    ),
                                )}
                                data-testid="enrollment-family-name-english-input"
                            />
                        </div>
                        <div className="text-right" dir="rtl">
                            <InputLabel>Family Name (Hebrew)</InputLabel>
                            <Input
                                value={formData.family.familyNameHebrew}
                                onChange={e => updateFamily('familyNameHebrew', e.target.value)}
                                className={getInputClass(formData.family.familyNameHebrew, initialDataRef.current.family.familyNameHebrew)}
                            />
                        </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                        <div className="md:col-span-2">
                            <InputLabel>Home Address</InputLabel>
                            <Input
                                value={formData.family.address}
                                onChange={e => updateFamily('address', e.target.value)}
                                onBlur={() => markFieldTouched(fieldKey('family', 'address'))}
                                className={joinClasses(
                                    getInputClass(formData.family.address, initialDataRef.current.family.address),
                                    getFieldIndicatorClassName(
                                        fieldKey('family', 'address'),
                                        formData.family.address,
                                    ),
                                )}
                            />
                        </div>
                        <div>
                            <InputLabel>City</InputLabel>
                            <Input
                                value={formData.family.city}
                                onChange={e => updateFamily('city', e.target.value)}
                                onBlur={() => markFieldTouched(fieldKey('family', 'city'))}
                                className={joinClasses(
                                    getInputClass(formData.family.city, initialDataRef.current.family.city),
                                    getFieldIndicatorClassName(
                                        fieldKey('family', 'city'),
                                        formData.family.city,
                                    ),
                                )}
                            />
                        </div>
                        <div className="grid grid-cols-2 gap-4">
                            <div>
                                <InputLabel>State</InputLabel>
                                <Select
                                    value={formData.family.state}
                                    options={US_STATES}
                                    onChange={val => updateFamily('state', val)}
                                    onBlur={() => markFieldTouched(fieldKey('family', 'state'))}
                                    searchable
                                    triggerClassName={joinClasses(
                                        getInputClass(formData.family.state, initialDataRef.current.family.state),
                                        getFieldIndicatorClassName(
                                            fieldKey('family', 'state'),
                                            formData.family.state,
                                        ),
                                    )}
                                />
                            </div>
                            <div>
                                <InputLabel>Zip</InputLabel>
                                <Input
                                    value={formData.family.zip}
                                    onChange={e => updateFamily('zip', e.target.value)}
                                    onBlur={() => markFieldTouched(fieldKey('family', 'zip'))}
                                    className={joinClasses(
                                        getInputClass(formData.family.zip, initialDataRef.current.family.zip),
                                        getFieldIndicatorClassName(
                                            fieldKey('family', 'zip'),
                                            formData.family.zip,
                                            {
                                                validator: (candidate) => readText(candidate).replace(/\D/g, '').length >= 5,
                                            },
                                        ),
                                    )}
                                />
                            </div>
                        </div>
                    </div>

                    <div>
                        <InputLabel>Home Phone</InputLabel>
                        <Input
                            type="tel"
                            inputMode="tel"
                            value={formData.family.homePhone}
                            onChange={e => updateFamily('homePhone', e.target.value)}
                            onBlur={() => markFieldTouched(fieldKey('family', 'homePhone'))}
                            className={joinClasses(
                                getInputClass(formData.family.homePhone, initialDataRef.current.family.homePhone),
                                getGroupedFieldIndicatorClassName(
                                    fieldKey('family', 'homePhone'),
                                    formData.family.homePhone,
                                    familyPhoneMissing,
                                    isValidPhoneNumber,
                                ),
                            )}
                        />
                    </div>
                </Card>
            </div>
        );
    };

    // Consolidated Parents Step
    const renderParentsStep = () => {
        const profileEmail = readText(profile?.email);
        const parentEmailMissing = !pickFirstValidEmail([
            formData.father.email,
            formData.mother.email,
            profileEmail,
        ]);
        const parentPhoneMissing = !pickFirstValidPhone([
            formData.family.homePhone,
            formData.father.cellPhone,
            formData.mother.cellPhone,
        ]);
        const hasCompleteParentName = Boolean(
            (readText(formData.father.firstName) && readText(formData.father.lastName))
            || (readText(formData.mother.firstName) && readText(formData.mother.lastName))
        );

        const renderParentCard = (type: 'father' | 'mother') => {
            const data = formData[type];
            const initial = initialDataRef.current[type];
            const title = type === 'father' ? "Father" : "Mother";
            const rowStarted = hasEnrollmentParentProfileInformation(data, { ignoredEmail: profileEmail });
            const firstNameRequired = !hasCompleteParentName || (rowStarted && !readText(data.firstName));
            const lastNameRequired = !hasCompleteParentName || (rowStarted && !readText(data.lastName));

            return (
                <Card>
                    <div className="mb-6 flex items-center gap-2 border-b border-gray-50 pb-2">
                        <div className={`p-1.5 rounded-lg ${type === 'father' ? 'bg-primary/10 text-primary' : 'bg-vibrant-coral/10 text-vibrant-coral'}`}>
                            {type === 'father' ? <Briefcase size={16} /> : <User size={16} />}
                        </div>
                        <span className={`text-xs font-bold uppercase tracking-widest ${type === 'father' ? 'text-primary' : 'text-vibrant-coral'}`}>{title}'s Information</span>
                    </div>

                    <div className="grid grid-cols-4 gap-4 mb-6">
                        <div className="col-span-1">
                            <InputLabel>Title</InputLabel>
                            <Select 
                                value={data.title}
                                options={['Mr.', 'Mrs.', 'Rabbi', 'Dr.', 'Ms.']}
                                onChange={val => updateParent(type, 'title', val)}
                                triggerClassName={getInputClass(data.title, initial.title)}
                            />
                        </div>
                        <div className="col-span-3 md:col-span-1">
                            <InputLabel>First Name</InputLabel>
                            <Input 
                                value={data.firstName}
                                onChange={e => updateParent(type, 'firstName', e.target.value)}
                                onBlur={() => markFieldTouched(fieldKey(type, 'firstName'))}
                                className={joinClasses(
                                    getInputClass(data.firstName, initial.firstName),
                                    getFieldIndicatorClassName(
                                        fieldKey(type, 'firstName'),
                                        data.firstName,
                                        { required: firstNameRequired },
                                    ),
                                )}
                            />
                        </div>
                         <div className="col-span-4 md:col-span-2">
                            <InputLabel>Last Name</InputLabel>
                            <Input 
                                value={data.lastName}
                                onChange={e => updateParent(type, 'lastName', e.target.value)}
                                onBlur={() => markFieldTouched(fieldKey(type, 'lastName'))}
                                className={joinClasses(
                                    getInputClass(data.lastName, initial.lastName),
                                    getFieldIndicatorClassName(
                                        fieldKey(type, 'lastName'),
                                        data.lastName,
                                        { required: lastNameRequired },
                                    ),
                                )}
                            />
                        </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                        <div>
                            <InputLabel>Occupation</InputLabel>
                            <Input 
                                value={data.occupation}
                                onChange={e => updateParent(type, 'occupation', e.target.value)}
                                className={getInputClass(data.occupation, initial.occupation)}
                            />
                        </div>
                        <div>
                            <InputLabel>Email</InputLabel>
                            <Input 
                                type="email"
                                value={data.email}
                                onChange={e => updateParent(type, 'email', e.target.value)}
                                onBlur={() => markFieldTouched(fieldKey(type, 'email'))}
                                className={joinClasses(
                                    getInputClass(data.email, initial.email),
                                    getFieldIndicatorClassName(
                                        fieldKey(type, 'email'),
                                        data.email,
                                        {
                                            required: parentEmailMissing,
                                            validator: isValidEmailAddress,
                                        },
                                    ),
                                )}
                            />
                        </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div>
                            <InputLabel>Work Phone</InputLabel>
                            <Input 
                                type="tel"
                                inputMode="tel"
                                value={data.workPhone}
                                onChange={e => updateParent(type, 'workPhone', e.target.value)}
                                onBlur={() => markFieldTouched(fieldKey(type, 'workPhone'))}
                                className={joinClasses(
                                    getInputClass(data.workPhone, initial.workPhone),
                                    getFieldIndicatorClassName(
                                        fieldKey(type, 'workPhone'),
                                        data.workPhone,
                                        {
                                            required: false,
                                            validator: isValidPhoneNumber,
                                        },
                                    ),
                                )}
                            />
                        </div>
                        <div>
                            <InputLabel>Cell Phone</InputLabel>
                            <Input 
                                type="tel"
                                inputMode="tel"
                                value={data.cellPhone}
                                onChange={e => updateParent(type, 'cellPhone', e.target.value)}
                                onBlur={() => markFieldTouched(fieldKey(type, 'cellPhone'))}
                                className={joinClasses(
                                    getInputClass(data.cellPhone, initial.cellPhone),
                                    getFieldIndicatorClassName(
                                        fieldKey(type, 'cellPhone'),
                                        data.cellPhone,
                                        {
                                            required: parentPhoneMissing,
                                            validator: isValidPhoneNumber,
                                        },
                                    ),
                                )}
                            />
                        </div>
                    </div>
                </Card>
            );
        };

        return (
            <div className="animate-in slide-in-from-bottom-4 fade-in duration-500">
                <SectionHeader 
                    icon={Users} 
                    title="Parents" 
                    subtitle="Please update details for both parents."
                />
                <div className="space-y-8">
                    {renderParentCard('father')}
                    {renderParentCard('mother')}
                </div>
            </div>
        );
    };

    const renderStudentsStep = () => (
        <div className="animate-in slide-in-from-bottom-4 fade-in duration-500">
             <SectionHeader 
                icon={Users} 
                title="Students to Register" 
                subtitle="Review the students below. Uncheck to withdraw a student from enrollment."
            />
            <Card className="overflow-hidden !p-0">
                <div className="overflow-x-auto">
                    <table className="w-full">
                        <thead className="bg-gray-50 border-b border-gray-100">
                            <tr>
                                <th className="px-6 py-4 text-left text-[10px] font-bold text-irongrey/40 uppercase tracking-widest w-16">Enrol</th>
                                <th className="px-6 py-4 text-left text-[10px] font-bold text-irongrey/40 uppercase tracking-widest">Student Name</th>
                                <th className="px-6 py-4 text-left text-[10px] font-bold text-irongrey/40 uppercase tracking-widest">Grade</th>
                                <th className="px-6 py-4 text-left text-[10px] font-bold text-irongrey/40 uppercase tracking-widest">DOB</th>
                                <th className="px-6 py-4 text-right text-[10px] font-bold text-irongrey/40 uppercase tracking-widest">Hebrew Name</th>
                                <th className="px-6 py-4 text-right text-[10px] font-bold text-irongrey/40 uppercase tracking-widest">Hebrew DOB</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-50">
                            {formData.students.map((student) => (
                                <tr key={student.id} className={`transition-colors ${student.isWithdrawn ? 'bg-red-50/30' : 'hover:bg-gray-50/50'}`}>
                                    <td className="px-6 py-4">
                                        <div className="flex items-center justify-center">
                                        <input 
                                            type="checkbox" 
                                            checked={!student.isWithdrawn} 
                                            onChange={() => handleWithdrawClick(student.id, student.isWithdrawn)}
                                                className="h-4 w-4 rounded border-gray-300 text-primary accent-primary focus:ring-primary focus:ring-offset-0 focus:outline-none cursor-pointer transition-all"
                                                aria-label={`Include ${student.englishName} in enrollment`}
                                        />
                                        </div>
                                    </td>
                                    <td className={`px-6 py-4 font-semibold text-sm ${student.isWithdrawn ? 'text-irongrey/40 line-through' : 'text-irongrey'}`}>
                                        {student.englishName}
                                    </td>
                                    <td className={`px-6 py-4 text-sm ${student.isWithdrawn ? 'text-irongrey/40' : 'text-irongrey'}`}>
                                        <StatusBadge status={student.grade} />
                                    </td>
                                    <td className={`px-6 py-4 text-sm ${student.isWithdrawn ? 'text-irongrey/40' : 'text-irongrey'}`}>
                                        {student.dob}
                                    </td>
                                    <td className={`px-6 py-4 text-sm text-right font-serif ${student.isWithdrawn ? 'text-irongrey/40' : 'text-irongrey'}`}>
                                        {student.hebrewName}
                                    </td>
                                    <td className={`px-6 py-4 text-sm text-right ${student.isWithdrawn ? 'text-irongrey/40' : 'text-irongrey'}`}>
                                        {student.hebrewDob}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </Card>

            <Modal
                isOpen={withdrawModalOpen}
                onClose={() => setWithdrawModalOpen(false)}
                title="Withdraw Student?"
                description="This will update the enrollment status for this student."
                footer={
                    <>
                        <Button variant="ghost" onClick={() => setWithdrawModalOpen(false)}>Cancel</Button>
                        <Button variant="danger" onClick={confirmWithdraw}>Confirm Withdrawal</Button>
                    </>
                }
            >
                <div className="bg-strawberry/5 rounded-xl p-4 flex items-start gap-3">
                    <AlertCircle className="text-strawberry shrink-0 mt-0.5" />
                    <div>
                        <p className="text-sm font-bold text-strawberry">Warning</p>
                        <p className="text-sm text-strawberry/80 mt-1">
                            Are you sure you want to withdraw <strong>{formData.students.find(s => s.id === studentToWithdraw)?.englishName}</strong>? 
                            This action indicates they will not be attending the upcoming term.
                        </p>
                    </div>
                </div>
            </Modal>
        </div>
    );

    const renderGrandparentsStep = () => (
        <div className="animate-in slide-in-from-bottom-4 fade-in duration-500">
             <SectionHeader 
                icon={Users} 
                title="Grandparents" 
                subtitle="Optional: Provide contact info for grandparents."
            />
            
            <div className="space-y-8">
                {/* Paternal */}
                <Card>
                    <div className="mb-4 font-bold text-primary uppercase tracking-widest text-xs">Paternal Grandparents (Father's Parents)</div>
                    <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-4">
                        <div className="col-span-1">
                            <InputLabel>Title</InputLabel>
                            <Input 
                                value={formData.paternalGrandparents.title} 
                                onChange={e => updateGrandparent('paternalGrandparents', 'title', e.target.value)}
                                placeholder="Rabbi & Mrs."
                            />
                        </div>
                        <div className="col-span-3">
                            <InputLabel>Name(s)</InputLabel>
                            <Input 
                                value={formData.paternalGrandparents.name} 
                                onChange={e => updateGrandparent('paternalGrandparents', 'name', e.target.value)}
                            />
                        </div>
                    </div>
                    <div className="mb-4">
                        <InputLabel>Address</InputLabel>
                        <Input 
                            value={formData.paternalGrandparents.address} 
                            onChange={e => updateGrandparent('paternalGrandparents', 'address', e.target.value)}
                        />
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                        <div><InputLabel>City</InputLabel><Input value={formData.paternalGrandparents.city} onChange={e => updateGrandparent('paternalGrandparents', 'city', e.target.value)} /></div>
                        <div>
                            <InputLabel>State</InputLabel>
                            <Select
                                options={US_STATES}
                                value={formData.paternalGrandparents.state}
                                onChange={val => updateGrandparent('paternalGrandparents', 'state', val)}
                                searchable
                            />
                        </div>
                        <div><InputLabel>Zip</InputLabel><Input value={formData.paternalGrandparents.zip} onChange={e => updateGrandparent('paternalGrandparents', 'zip', e.target.value)} /></div>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                            <InputLabel>Phone</InputLabel>
                            <Input
                                type="tel"
                                inputMode="tel"
                                value={formData.paternalGrandparents.phone}
                                onChange={e => updateGrandparent('paternalGrandparents', 'phone', e.target.value)}
                                onBlur={() => markFieldTouched(fieldKey('paternalGrandparents', 'phone'))}
                                className={getFieldIndicatorClassName(
                                    fieldKey('paternalGrandparents', 'phone'),
                                    formData.paternalGrandparents.phone,
                                    { required: false, validator: isValidPhoneNumber },
                                )}
                            />
                        </div>
                        <div>
                            <InputLabel>Email</InputLabel>
                            <Input
                                type="email"
                                value={formData.paternalGrandparents.email}
                                onChange={e => updateGrandparent('paternalGrandparents', 'email', e.target.value)}
                                onBlur={() => markFieldTouched(fieldKey('paternalGrandparents', 'email'))}
                                className={getFieldIndicatorClassName(
                                    fieldKey('paternalGrandparents', 'email'),
                                    formData.paternalGrandparents.email,
                                    { required: false, validator: isValidEmailAddress },
                                )}
                            />
                        </div>
                    </div>
                </Card>

                {/* Maternal */}
                <Card>
                    <div className="mb-4 font-bold text-primary uppercase tracking-widest text-xs">Maternal Grandparents (Mother's Parents)</div>
                    <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-4">
                        <div className="col-span-1">
                            <InputLabel>Title</InputLabel>
                            <Input 
                                value={formData.maternalGrandparents.title} 
                                onChange={e => updateGrandparent('maternalGrandparents', 'title', e.target.value)}
                                placeholder="Mr. & Mrs."
                            />
                        </div>
                        <div className="col-span-3">
                            <InputLabel>Name(s)</InputLabel>
                            <Input 
                                value={formData.maternalGrandparents.name} 
                                onChange={e => updateGrandparent('maternalGrandparents', 'name', e.target.value)}
                            />
                        </div>
                    </div>
                    <div className="mb-4">
                        <InputLabel>Address</InputLabel>
                        <Input 
                            value={formData.maternalGrandparents.address} 
                            onChange={e => updateGrandparent('maternalGrandparents', 'address', e.target.value)}
                        />
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                        <div><InputLabel>City</InputLabel><Input value={formData.maternalGrandparents.city} onChange={e => updateGrandparent('maternalGrandparents', 'city', e.target.value)} /></div>
                        <div>
                            <InputLabel>State</InputLabel>
                            <Select
                                options={US_STATES}
                                value={formData.maternalGrandparents.state}
                                onChange={val => updateGrandparent('maternalGrandparents', 'state', val)}
                                searchable
                            />
                        </div>
                        <div><InputLabel>Zip</InputLabel><Input value={formData.maternalGrandparents.zip} onChange={e => updateGrandparent('maternalGrandparents', 'zip', e.target.value)} /></div>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                            <InputLabel>Phone</InputLabel>
                            <Input
                                type="tel"
                                inputMode="tel"
                                value={formData.maternalGrandparents.phone}
                                onChange={e => updateGrandparent('maternalGrandparents', 'phone', e.target.value)}
                                onBlur={() => markFieldTouched(fieldKey('maternalGrandparents', 'phone'))}
                                className={getFieldIndicatorClassName(
                                    fieldKey('maternalGrandparents', 'phone'),
                                    formData.maternalGrandparents.phone,
                                    { required: false, validator: isValidPhoneNumber },
                                )}
                            />
                        </div>
                        <div>
                            <InputLabel>Email</InputLabel>
                            <Input
                                type="email"
                                value={formData.maternalGrandparents.email}
                                onChange={e => updateGrandparent('maternalGrandparents', 'email', e.target.value)}
                                onBlur={() => markFieldTouched(fieldKey('maternalGrandparents', 'email'))}
                                className={getFieldIndicatorClassName(
                                    fieldKey('maternalGrandparents', 'email'),
                                    formData.maternalGrandparents.email,
                                    { required: false, validator: isValidEmailAddress },
                                )}
                            />
                        </div>
                    </div>
                </Card>
            </div>
        </div>
    );

    const renderSiblingsStep = () => (
        <div className="animate-in slide-in-from-bottom-4 fade-in duration-500">
             <SectionHeader 
                icon={Baby} 
                title="Other Siblings" 
                subtitle="List other children in the family not currently registering."
            />
            <Card>
                {formData.siblings.length === 0 && (
                     <div className="text-center py-8 text-irongrey/40 text-sm">
                        No siblings added yet. Click below to add.
                    </div>
                )}
                
                {formData.siblings.map((sibling, index) => (
                    <div key={sibling.id} className="mb-6 pb-6 border-b border-gray-50 md:border-0 md:mb-4 md:pb-0 md:flex md:gap-4 md:items-end animate-in fade-in slide-in-from-left-2 duration-300 last:border-0 last:pb-0">
                        {/* Mobile Row 1: Name + Trash */}
                        <div className="flex gap-3 items-end w-full md:w-auto md:flex-1">
                            <div className="flex-1">
                                <InputLabel>Name</InputLabel>
                                <Input value={sibling.name} onChange={e => updateSibling(sibling.id, 'name', e.target.value)} placeholder="Name" />
                            </div>
                            <button 
                                onClick={() => removeSibling(sibling.id)}
                                className="md:hidden h-[42px] w-[42px] shrink-0 flex items-center justify-center rounded-xl bg-strawberry/5 text-strawberry hover:bg-strawberry/20 transition-colors"
                            >
                                <Trash2 size={18} />
                            </button>
                        </div>
                        
                        {/* Mobile Row 2: Age + School */}
                        <div className="flex gap-3 mt-3 md:mt-0 w-full md:w-auto md:contents">
                            <div className="w-24 shrink-0">
                                <InputLabel>Age</InputLabel>
                                <Input type="number" value={sibling.age} onChange={e => updateSibling(sibling.id, 'age', e.target.value)} placeholder="Age" />
                            </div>
                            <div className="flex-1">
                                <InputLabel>School / Yeshiva</InputLabel>
                                <Input value={sibling.school} onChange={e => updateSibling(sibling.id, 'school', e.target.value)} placeholder="Current Institution" />
                            </div>
                        </div>

                        {/* Desktop Trash */}
                        <button 
                            onClick={() => removeSibling(sibling.id)}
                            className="hidden md:flex h-[42px] w-[42px] shrink-0 items-center justify-center rounded-xl bg-strawberry/5 text-strawberry hover:bg-strawberry/20 transition-colors"
                        >
                            <Trash2 size={18} />
                        </button>
                    </div>
                ))}

                <Button variant="secondary" onClick={addSibling} icon={Plus} className="w-full mt-4 border-dashed">
                    Add Sibling Row
                </Button>
            </Card>
        </div>
    );

    const renderSignaturesStep = () => (
        <div className="animate-in slide-in-from-bottom-4 fade-in duration-500">
             <SectionHeader 
                icon={PenTool} 
                title="Authorization & Signatures" 
                subtitle="Each listed parent must sign to complete registration."
            />

            <Card className="mb-6">
                <div className="bg-gray-50 p-4 rounded-xl border border-gray-100 mb-6 text-sm text-irongrey/80 leading-relaxed">
                    By signing below, we confirm that all information provided is accurate and we agree to the school's enrollment policies, financial obligations, and code of conduct for the upcoming school year.
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-8">
                    <SignaturePad 
                        label="Father's Signature" 
                        value={formData.signatures.fatherSignature} 
                        onChange={val => setFormData(prev => ({...prev, signatures: {...prev.signatures, fatherSignature: val}}))}
                        className={submitAttempted && signatureRequirement.missingParents.includes('father') ? 'border-amber-700 bg-amber-50' : ''}
                    />
                    <SignaturePad 
                        label="Mother's Signature" 
                        value={formData.signatures.motherSignature} 
                        onChange={val => setFormData(prev => ({...prev, signatures: {...prev.signatures, motherSignature: val}}))}
                        className={submitAttempted && signatureRequirement.missingParents.includes('mother') ? 'border-amber-700 bg-amber-50' : ''}
                    />
                </div>

                <div className="flex justify-end border-t border-gray-100 pt-6">
                    <div className="w-48">
                         <InputLabel>Date</InputLabel>
                         <Input value={formData.signatures.date} disabled className="bg-gray-100 text-irongrey/60" />
                    </div>
                </div>
            </Card>
        </div>
    );

    const renderSuccess = () => (
        <div className="text-center py-20 animate-in fade-in zoom-in-95 duration-500">
            <div className="w-24 h-24 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-6">
                <CheckCircle size={48} className="text-primary" />
            </div>
            <h2 className="text-3xl font-bold text-irongrey mb-4 font-heading">Registration Complete!</h2>
            <p className="text-irongrey/60 max-w-md mx-auto mb-8">
                Your forms have been generated and sent to the school office. 
                A copy has also been emailed to {formData.father.email} and {formData.mother.email}.
            </p>
            {submittedCode ? (
                <p className="text-xs font-bold text-primary uppercase tracking-wide mb-4">Application ref: {submittedCode}</p>
            ) : null}
            <div className="flex flex-wrap justify-center gap-4">
                <Button variant="secondary" icon={FileText} onClick={() => { void handleDownloadPdf(); }}>Download PDF</Button>
                <Button variant="secondary" icon={PenTool} onClick={openSubmittedRegistrationEditor}>Edit Registration</Button>
                <Button variant="ghost" onClick={() => { navigate('/parent/portal/dashboard'); }}>Return to Dashboard</Button>
            </div>
        </div>
    );

    // Render Logic Based on New Step Order
    const renderStepContent = () => {
        switch(currentStep) {
            case 0: return renderFamilyStep();
            case 1: return renderParentsStep(); // Combined Father & Mother
            case 2: return renderGrandparentsStep();
            case 3: return renderSiblingsStep();
            case 4: return renderStudentsStep(); // Moved after Siblings
            case 5: return renderSignaturesStep();
            case 6: return renderSuccess();
            default: return null;
        }
    };

    if (!applicationId || !childApplicationId) {
        return (
            <div
                className="max-w-3xl mx-auto px-6 py-16"
                data-testid="parent-portal-enrollment-intake-missing-target"
            >
                <Card>
                    <SectionHeader
                        icon={AlertCircle}
                        title="Select a student to continue enrollment"
                        subtitle="Enrollment intake appends to an accepted admissions profile and needs a selected student context."
                    />
                    <div className="flex items-center justify-end">
                        <Link
                            to="/parent/portal/enrollment"
                            className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-jungle text-white text-sm font-bold hover:opacity-90 transition-opacity"
                        >
                            Go to Enrollment
                            <ChevronRight size={14} />
                        </Link>
                    </div>
                </Card>
            </div>
        );
    }

    if (applicationQuery.isLoading && !applicationQuery.data) {
        return (
            <div className="max-w-3xl mx-auto px-6 py-16">
                <Card>
                    <p className="text-sm font-semibold text-irongrey/70">Loading enrollment profile...</p>
                </Card>
            </div>
        );
    }

    if (applicationQuery.error || !selectedChild.id) {
        return (
            <div className="max-w-3xl mx-auto px-6 py-16" data-testid="parent-portal-enrollment-intake-invalid-target">
                <Card>
                    <SectionHeader
                        icon={AlertCircle}
                        title="Enrollment profile not found"
                        subtitle="We could not locate this admissions profile for enrollment."
                    />
                    <div className="flex items-center justify-end">
                        <Link
                            to="/parent/portal/enrollment"
                            className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-jungle text-white text-sm font-bold hover:opacity-90 transition-opacity"
                        >
                            Back to Enrollment
                            <ChevronRight size={14} />
                        </Link>
                    </div>
                </Card>
            </div>
        );
    }

    if (!selectedChildEligible) {
        return (
            <div className="max-w-3xl mx-auto px-6 py-16" data-testid="parent-portal-enrollment-intake-ineligible-target">
                <Card>
                    <SectionHeader
                        icon={AlertCircle}
                        title="Enrollment is not available yet"
                        subtitle="This student must be accepted by school staff before enrollment can begin."
                    />
                    <p className="text-sm text-irongrey/70 mb-4">
                        Current status: {selectedChildStatus || 'Unknown'}
                    </p>
                    <div className="flex items-center justify-end">
                        <Link
                            to="/parent/portal/enrollment"
                            className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-jungle text-white text-sm font-bold hover:opacity-90 transition-opacity"
                        >
                            Back to Enrollment
                            <ChevronRight size={14} />
                        </Link>
                    </div>
                </Card>
            </div>
        );
    }

    return (
        <div className="max-w-5xl mx-auto px-0 py-0 pb-32 sm:px-6 sm:py-10">
            <Modal
                isOpen={browserBackGuard.isPromptOpen}
                onClose={browserBackGuard.cancelNavigation}
                title="Leave this enrollment form?"
                description="Browser back can clear unsaved form data."
                footer={(
                    <div className="flex flex-col-reverse gap-3 sm:flex-row sm:justify-end">
                        <Button variant="secondary" onClick={browserBackGuard.cancelNavigation}>
                            Stay on form
                        </Button>
                        <Button variant="danger" onClick={browserBackGuard.confirmNavigation}>
                            Leave page
                        </Button>
                    </div>
                )}
            >
                <p className="text-sm font-semibold text-irongrey/70">
                    Use the enrollment back buttons when you want to move between
                    form steps. Leaving with the browser back button can clear
                    details that have not been submitted yet.
                </p>
            </Modal>

            {currentStep < 6 && <ProgressBar currentStep={currentStep} onStepClick={setCurrentStep} />}
            
            {renderStepContent()}

            <div className="border-t border-gray-100 mb-8 mt-12"></div>
            {submissionError ? (
                <SupportErrorMessage className="mb-6">
                    Submission failed: {submissionError}
                </SupportErrorMessage>
            ) : null}

            <div className="flex items-center justify-between">
                {currentStep > 0 && currentStep < 6 && (
                    <button onClick={prevStep} className="flex items-center gap-2 px-6 py-3 border border-gray-200 rounded-xl text-irongrey font-semibold text-sm hover:bg-gray-50 transition-colors">
                        <ChevronLeft size={16} />
                        Previous Step
                    </button>
                )}
                
                {currentStep === 0 && (
                    <Button
                                        variant="secondary"
                                        icon={ArrowLeftToLine}
                                        className="!bg-gray-100 !text-irongrey !border !border-gray-200 hover:!bg-gray-200"
                                        onClick={goToChecklist}
                                    >
                                        Checklist
                                    </Button>
                )}

                {currentStep < 5 && (
                    <Button variant="primary" onClick={nextStep} className="!py-3">
                        Save & Continue
                        <ChevronRight size={16} />
                    </Button>
                )}

                {currentStep === 5 && (
                     <Button 
                        variant="primary" 
                        onClick={() => { void handleSubmitRegistration(); }}
                        disabled={isSubmitting}
                        className="!bg-jungle hover:!bg-primary !py-3"
                    >
                        {isSubmitting ? 'Submitting…' : 'Submit Registration'}
                        <CheckCircle size={16} />
                    </Button>
                )}
            </div>
        </div>
    );
};

export default StudentForm;
